import React from 'react';
import { useTranslation } from 'react-i18next';
import { ProgressSpinner } from 'primereact/progressspinner';
import { formatDateOnly } from '../../../../../../utils/dateUtils';
import WarrantyStatusBanner from '../components/WarrantyStatusBanner';
import GenericFileUpload from '../../../../../../components/common/GenericFileUpload';

const DetailInfoView = ({
    formData,
    chassisData,
    documents = [],
    isLoadingDocuments = false,
    onDownloadDocument
}) => {
    const { t } = useTranslation();

    const displayBrand = chassisData?.brand || (formData?.brand !== 0 ? formData?.brand : '-');
    const displayModel = chassisData?.model || (formData?.model !== 0 ? formData?.model : '-');
    const warrantyBegin = formatDateOnly(chassisData?.warrantyBeginDate || formData?.warrantyBeginDate) || '-';
    const warrantyEnd = formatDateOnly(chassisData?.warrantyEndDate || formData?.warrantyEndDate) || '-';
    const registrationDate = formatDateOnly(chassisData?.registrationDate) || '-';
    const deliveryDate = formatDateOnly(formData?.serviceDeliveryDate) || '-';
    const displayKilometer = formData?.kmInformation;

    const receivedBy = formData?.receivingPerson;
    const deliveredBy = formData?.receivingDeliveryPerson;
    const deliveryPhone = formData?.deliveryPhone;

    if (!formData) return <div className="text-center p-4">{t('common.noDataAvailable')}</div>;

    const SectionHeader = ({ icon, title }) => (
        <div className="card-header" style={{ background: '#273A81', color: 'white', padding: '1rem 1.5rem', borderRadius: '12px 12px 0 0' }}>
            <div className="flex align-items-center">
                <i className={`${icon} mr-3`} style={{ fontSize: '1.25rem' }}></i>
                <h6 className="m-0 font-semibold" style={{ color: 'white' }}>{title}</h6>
            </div>
        </div>
    );

    const InfoField = ({ icon, label, value, fullWidth = false }) => (
        <div className={fullWidth ? 'col-12' : 'col-12 md:col-6'}>
            <div className="field-container" style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #e9ecef', marginBottom: '0.3rem', height: '100%' }}>
                <label className="field-label" style={{ color: '#273A81', fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                    <i className={`${icon} mr-2`} style={{ color: '#5D72B2' }}></i>
                    {label}
                </label>
                <p className="field-value" style={{ color: '#2c3e50', fontSize: '1rem', fontWeight: '500', margin: '0', wordBreak: 'break-word' }}>
                    {value || '-'}
                </p>
            </div>
        </div>
    );

    return (
        <div className="p-fluid">
            <WarrantyStatusBanner
                startDate={chassisData?.warrantyBeginDate || formData?.warrantyBeginDate}
                endDate={chassisData?.warrantyEndDate || formData?.warrantyEndDate}
                checkDate={formData?.serviceDeliveryDate}
            />

            <div className="grid">
                {/* Araç Bilgileri */}
                <div className="col-12 lg:col-6">
                    <div>
                        <SectionHeader icon="pi pi-car" title={t('warranty.customerDeclaration.dialog.sections.vehicleInfo')} />
                        <div className="card-body" style={{ padding: '0.5rem' }}>
                            <div className="grid">
                                <InfoField
                                    icon="pi pi-hashtag"
                                    label={t('warranty.customerDeclaration.dialog.fields.chassisNumber')}
                                    value={formData.chassisNumber}
                                    fullWidth
                                />
                                <InfoField
                                    icon="pi pi-box"
                                    label={t('warranty.customerDeclaration.dialog.fields.brand')}
                                    value={displayBrand}
                                />
                                <InfoField
                                    icon="pi pi-box"
                                    label={t('warranty.customerDeclaration.dialog.fields.model')}
                                    value={displayModel}
                                />
                                <InfoField
                                    icon="pi pi-calendar-plus"
                                    label={t('warranty.customerDeclaration.dialog.fields.warrantyBeginDate')}
                                    value={warrantyBegin}
                                />
                                <InfoField
                                    icon="pi pi-calendar-minus"
                                    label={t('warranty.customerDeclaration.dialog.fields.warrantyEndDate')}
                                    value={warrantyEnd}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Müşteri Bilgileri */}
                <div className="col-12 lg:col-6">
                    <div>
                        <SectionHeader icon="pi pi-user" title={t('warranty.customerDeclaration.dialog.sections.customerInfo')} />
                        <div className="card-body" style={{ padding: '0.5rem' }}>
                            <div className="grid">
                                <InfoField
                                    icon="pi pi-user"
                                    label={t('warranty.customerDeclaration.dialog.fields.customerId')}
                                    value={formData?.customerNameSurname || formData?.customerName || chassisData?.customerName}
                                    fullWidth
                                />
                                <InfoField
                                    icon="pi pi-phone"
                                    label={t('warranty.customerDeclaration.dialog.fields.customerPhone')}
                                    value={formData?.customerPhone || chassisData?.customerPhone}
                                    fullWidth
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tarih Bilgileri */}
                <div className="col-12 lg:col-6">
                    <div>
                        <SectionHeader icon="pi pi-calendar" title={t('warranty.customerDeclaration.dialog.sections.dateInfo')} />
                        <div className="card-body" style={{ padding: '0.5rem' }}>
                            <div className="grid">
                                <InfoField
                                    icon="pi pi-calendar-plus"
                                    label={t('warranty.customerDeclaration.dialog.fields.serviceDeliveryDate')}
                                    value={deliveryDate}
                                    fullWidth
                                />
                                <InfoField
                                    icon="pi pi-verified"
                                    label={t('warranty.customerDeclaration.dialog.fields.registrationDate')}
                                    value={registrationDate}
                                    fullWidth
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Teslim Bilgileri */}
                <div className="col-12 lg:col-6">
                    <div>
                        <SectionHeader icon="pi pi-users" title={t('warranty.customerDeclaration.dialog.sections.deliveryInfo')} />
                        <div className="card-body" style={{ padding: '0.5rem' }}>
                            <div className="grid">
                                <InfoField
                                    icon="pi pi-download"
                                    label={t('warranty.customerDeclaration.dialog.fields.receivedBy')}
                                    value={receivedBy}
                                    fullWidth
                                />
                                <InfoField
                                    icon="pi pi-upload"
                                    label={t('warranty.customerDeclaration.dialog.fields.deliveredBy')}
                                    value={deliveredBy}
                                    fullWidth
                                />
                                <InfoField
                                    icon="pi pi-upload"
                                    label={t('warranty.customerDeclaration.dialog.fields.deliveryPhone')}
                                    value={deliveryPhone}
                                    fullWidth
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Açıklama */}
                <div className="col-12 lg:col-6">
                    <div>
                        <SectionHeader icon="pi pi-file-text" title={t('warranty.customerDeclaration.dialog.sections.description')} />
                        <div className="card-body" style={{ padding: '0.5rem' }}>
                            <div className="grid">
                                <InfoField
                                    icon="pi pi-pencil"
                                    label={t('warranty.customerDeclaration.dialog.fields.declaration')}
                                    value={formData.customerStatement}
                                    fullWidth
                                />
                                <InfoField
                                    icon="pi pi-file-edit"
                                    label={t('warranty.customerDeclaration.dialog.fields.serviceNote')}
                                    value={formData.serviceNote}
                                    fullWidth
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* KM ve Plaka */}
                <div className="col-12 lg:col-6">
                    <div>
                        <SectionHeader icon="pi pi-compass" title={t('warranty.customerDeclaration.dialog.sections.kmAndOtherInfo')} />
                        <div className="card-body" style={{ padding: '0.5rem' }}>
                            <div className="grid">
                                <InfoField
                                    icon="pi pi-compass"
                                    label={t('warranty.customerDeclaration.dialog.fields.kilometer')}
                                    value={displayKilometer ? `${displayKilometer.toLocaleString('tr-TR')} km` : '-'}
                                />
                                <InfoField
                                    icon="pi pi-credit-card"
                                    label={t('warranty.customerDeclaration.dialog.fields.plateNumber')}
                                    value={chassisData?.plateNumber || formData?.plateNumber}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Yüklü Dosyalar */}
                <div className="col-12">
                    <div>
                        <SectionHeader icon="pi pi-file" title={t('common.files')} />
                        <div className="card-body" style={{ padding: '1rem' }}>
                            {isLoadingDocuments ? (
                                <div className="flex justify-content-center p-4">
                                    <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                                </div>
                            ) : documents && documents.length > 0 ? (
                                <GenericFileUpload
                                    newFiles={[]}
                                    onFilesChange={() => { }}
                                    existingDocuments={documents}
                                    onDownloadDocument={onDownloadDocument}
                                    onDeleteExistingDocument={null}
                                    onUpdateExistingDocument={null}
                                    isLoading={false}
                                    disabled={true}
                                    multiple={false}
                                    showDownloadButton={true}
                                    showUpdateButton={false}
                                    showDeleteButton={false}
                                    readOnlyMode={true}
                                />
                            ) : (
                                <div className="text-center p-4 text-500">
                                    <i className="pi pi-inbox" style={{ fontSize: '3rem', color: '#cbd5e1' }}></i>
                                    <p className="mt-3 mb-0">{t('warranty.dealerVehicleRegistrationForm.documentManager.table.emptyMessage')}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DetailInfoView;